import { jsxs, jsx } from 'react/jsx-runtime';
import { useState, useRef, useEffect, useCallback, useContext, createContext } from 'react';
import { SimplePopup } from './components/SimplePopup.js';

const PopupContext = /*#__PURE__*/createContext({
  open: () => null,
  close: () => null
});
const INITIAL_POPUP_STATE = {
  popupId: 0,
  config: null,
  visible: false
};
function PopupProvider(_ref) {
  let {
    children
  } = _ref;
  const [popupState, setPopupState] = useState(INITIAL_POPUP_STATE);
  const configRef = useRef(null);
  const pendingReopenRef = useRef(null);
  useEffect(() => () => {
    if (pendingReopenRef.current !== null) {
      clearTimeout(pendingReopenRef.current);
    }
  }, []);
  const cancelPendingReopen = useCallback(() => {
    if (pendingReopenRef.current !== null) {
      clearTimeout(pendingReopenRef.current);
      pendingReopenRef.current = null;
    }
  }, []);
  const fireOnClose = useCallback(() => {
    var _a;
    const onClose = (_a = configRef.current) === null || _a === void 0 ? void 0 : _a.onClose;
    configRef.current = null;
    onClose === null || onClose === void 0 ? void 0 : onClose();
  }, []);
  const closePopup = useCallback(() => {
    cancelPendingReopen();
    fireOnClose();
    setPopupState(current => {
      if (!current.config || !current.visible) {
        return current;
      }
      return Object.assign(Object.assign({}, current), {
        config: null,
        visible: false
      });
    });
  }, [cancelPendingReopen, fireOnClose]);
  const openPopup = useCallback(config => {
    cancelPendingReopen();
    fireOnClose();
    configRef.current = config;
    setPopupState(current => {
      const nextId = current.popupId + 1;
      if (current.visible) {
        pendingReopenRef.current = setTimeout(() => {
          pendingReopenRef.current = null;
          setPopupState(prev => Object.assign(Object.assign({}, prev), {
            config,
            visible: true
          }));
        }, 0);
        return Object.assign(Object.assign({}, current), {
          popupId: nextId,
          config,
          visible: false
        });
      }
      return Object.assign(Object.assign({}, current), {
        popupId: nextId,
        config,
        visible: true
      });
    });
  }, [cancelPendingReopen, fireOnClose]);
  const handleHide = useCallback(popupId => {
    setPopupState(current => {
      if (current.popupId !== popupId || !current.config) {
        return current;
      }
      fireOnClose();
      return Object.assign(Object.assign({}, current), {
        config: null,
        visible: false
      });
    });
  }, [fireOnClose]);
  return jsxs(PopupContext.Provider, {
    value: {
      open: openPopup,
      close: closePopup
    },
    children: [children, jsx(SimplePopup, {
      config: popupState.config,
      visible: popupState.visible,
      onHide: () => {
        handleHide(popupState.popupId);
      }
    })]
  });
}
function usePopup() {
  return useContext(PopupContext);
}
function useTogglePopup() {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;
  const {
    open,
    close
  } = usePopup();
  const toggle = useCallback((e, config) => {
    e.preventDefault();
    e.stopPropagation();
    if (visibleRef.current) {
      close();
      return;
    }
    setVisible(true);
    open(Object.assign(Object.assign({}, config), {
      target: e.currentTarget,
      onClose: () => setVisible(false)
    }));
  }, [open, close]);
  return {
    visible,
    toggle
  };
}

export { PopupProvider, usePopup, useTogglePopup };
